import type { Cohort, Module } from '@efdigital/ll-addon-data';
import { DataTypeXceed } from '@efdigital/ll-addon-data/data-types';
import { addOne } from '@efdigital/ll-admin-v2/helpers/add-one.ts';
import { generateRandomSlug } from '@efdigital/ll-admin-v2/lib/random-slug';
import type { ParamsProgramId } from '@efdigital/ll-admin-v2/routes/programs/program';
import type { ParamsCohortId } from '@efdigital/ll-admin-v2/routes/programs/program/cohort';
import { array } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { set } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { type Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import { DragToSort } from '../drag-to-sort.gts';
import { EditRecord, type EditRecordSignature } from '../edit-record.gts';

export class EditCohortModules extends Component<
  EditRecordSignature<Cohort, { params: ParamsProgramId & ParamsCohortId }>
> {
  @service declare store: Store;

  setModuleValue = (property: string, module: Module, event: Event) => {
    const target = event.target as HTMLInputElement;
    set(module, property, target.value);
  };

  removeModuleAt = (modules: Module[], index: number) => {
    const removed = modules.splice(index, 1);
    if (removed.length > 0) {
      // Immediate rollback before removing from relationship
      removed[0]!.rollbackAttributes();
    }
  };

  newModule = async () => {
    const module = this.store.createRecord<Module>(DataTypeXceed.Module, {
      slug: generateRandomSlug(),
      program: (await this.args.item.program)!,
      cohort: this.args.item,
      verb: 'http://adlnet.gov/expapi/verbs/experienced',
      activityType: 'http://adlnet.gov/expapi/activities/module',
    });

    const modules = await this.args.item.modules;
    modules.push(module);
  };

  <template>
    <EditRecord
      @editLabel="Edit Modules"
      @item={{@item}}
      @context={{@context}}
      ...attributes
    >
      <:form as |uniqueId isDisabled isReadOnly|>
        <fieldset
          disabled={{isDisabled}}
          class="mb-0"
          id="{{uniqueId}}-modules"
        >
          <legend class="gud-eyebrow">Modules</legend>

          {{#if isReadOnly}}
            <Await @promise={{@item.modules}}>
              <:success as |modules|>
                {{#if modules.length}}
                  <div class="list-group mb-3">
                    {{#each modules as |module|}}
                      <LinkTo
                        @route="programs.program.cohort.content.module.index"
                        @models={{array
                          @params.program_id
                          @params.cohort_id
                          module.id
                        }}
                        class="list-group-item"
                      >{{module.title}}
                        <iconify-icon inline icon="ph:arrow-right" /></LinkTo>
                    {{/each}}
                  </div>
                {{else}}
                  <p>No modules.</p>
                {{/if}}
              </:success>
            </Await>
          {{else}}
            <Await @promise={{@item.modules}}>
              <:success as |modules|>
                <div class="list-group mb-3 gap-1">
                  <DragToSort @items={{modules}}>
                    <:item as |dropZone draggable module index|>
                      <div class="input-group" {{dropZone index}}>
                        <span
                          class="input-group-text justify-content-center fw-bold rounded-start-2 drag-handle w-3rem"
                          {{draggable module index}}
                          title="Drag to reorder"
                        >
                          {{addOne index}}
                        </span>
                        <input
                          type="text"
                          class="form-control list-group-item"
                          value={{module.title}}
                          aria-label="Module title"
                          required
                          {{on
                            "change"
                            (fn this.setModuleValue "title" module)
                          }}
                        />
                        <button
                          aria-label="Remove module"
                          class="btn btn-outline-primary rounded-end-2"
                          type="button"
                          {{on "click" (fn this.removeModuleAt modules index)}}
                        ><iconify-icon inline icon="ph:trash-bold" /></button>
                      </div>
                    </:item>
                  </DragToSort>
                </div>

              </:success>
            </Await>
          {{/if}}
        </fieldset>
      </:form>
      <:buttons as |uniqueId isReadOnly|>
        {{#unless isReadOnly}}
          <button
            type="button"
            class="btn btn-primary btn-sm"
            id="{{uniqueId}}-newModule"
            {{on "click" this.newModule}}
          >New module</button>
        {{/unless}}
      </:buttons>
    </EditRecord>
  </template>
}
